import type { EldrickPayload, GearSpecs } from '../equipment/types';
import { unwrapValue } from '../equipment/types';
import { CLUB_PRESETS } from '../constants';
import type { ClubPreset, SwingParams } from '../types';
import { createSwingParams, deriveLaunchDirection, deriveSpinAxisFromImpact } from '../simulation/SwingInput';

/** Validate the existing Core contract before anything reaches the simulation.
 * Missing measurements are allowed, but malformed measurements are never coerced. */
export function parseImportedFitting(input: unknown): EldrickPayload {
  if (!input || typeof input !== 'object') throw new Error('invalid_fitting');
  const p = input as EldrickPayload;
  if (!p.meta || typeof p.meta.fittingId !== 'string' || !p.meta.fittingId.trim() ||
      !['driver', 'iron'].includes(p.meta.fittingType ?? '') || !p.swing || !p.gear ||
      !p.currentSetup || !p.player || !p.swingProfile) throw new Error('invalid_fitting');
  for (const section of [p.swing, p.gear, p.currentSetup, p.player, p.swingProfile]) {
    if (typeof section !== 'object' || Array.isArray(section)) throw new Error('invalid_fitting');
  }
  const ranges: Record<string, [number, number]> = {
    clubheadSpeed: [1, 180], ballSpeed: [1, 250], launchAngle: [-15, 80],
    spinRate: [0, 20000], attackAngle: [-30, 30], clubPath: [-45, 45], faceAngle: [-45, 45],
    launchDirection: [-90, 90],
  };
  for (const [key, [min, max]] of Object.entries(ranges)) {
    const v = (p.swing as unknown as Record<string, unknown>)[key];
    if (v != null && (typeof v !== 'number' || !Number.isFinite(v) || v < min || v > max)) {
      throw new Error('invalid_fitting');
    }
  }
  if (p.swing.spinAxis != null) {
    const axis = unwrapValue(p.swing.spinAxis);
    if (typeof axis !== 'number' || !Number.isFinite(axis) || Math.abs(axis) > 180) throw new Error('invalid_fitting');
  }
  const gearRanges: Array<[unknown, number, number]> = [
    [p.gear.loft, 1, 70], [p.gear.shaftWeight, 20, 200], [p.gear.length, 25, 50],
    [unwrapValue(p.currentSetup.ballCompression), 1, 150],
  ];
  for (const [v, min, max] of gearRanges) {
    if (v != null && (typeof v !== 'number' || !Number.isFinite(v) || v < min || v > max)) throw new Error('invalid_fitting');
  }
  if (p.meta.objective != null && !['balanced', 'distance', 'accuracy'].includes(p.meta.objective)) throw new Error('invalid_fitting');
  if (p.swingSamples != null) {
    if (!Array.isArray(p.swingSamples) || p.swingSamples.length < 2 || p.swingSamples.length > 64) throw new Error('invalid_fitting');
    for (const swing of p.swingSamples) {
      parseImportedFitting({ ...p, swing, swingSamples: undefined });
      for (const key of ['clubheadSpeed', 'ballSpeed', 'launchAngle', 'launchDirection', 'spinRate', 'attackAngle', 'clubPath', 'faceAngle'] as const) {
        if (typeof swing[key] !== 'number') throw new Error('invalid_fitting');
      }
      if (typeof swing.spinAxis !== 'number') throw new Error('invalid_fitting');
    }
  }
  return p;
}

/** Map only vocabulary understood by the current model; never invent a catalog head. */
export function importedSpecDetails(payload: EldrickPayload): Partial<GearSpecs> {
  const gear = payload.gear;
  const pick = <const T extends string>(value: string | null, allowed: readonly T[], fallback: T): T => {
    const key = typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, '-') : '';
    return allowed.includes(key as T) ? key as T : fallback;
  };
  return {
    clubLength: gear.length ?? (payload.meta.fittingType === 'iron' ? 37 : 45.5),
    swingWeight: typeof gear.swingWeight === 'string' && /^[A-F][0-9]$/.test(gear.swingWeight) ? gear.swingWeight : 'D3',
    launchProfile: pick(gear.launchProfile, ['low', 'mid-low', 'mid', 'mid-high', 'high'], 'mid'),
    weightSetting: pick(gear.weightSetting, ['standard', 'draw', 'fade', 'back', 'front'], 'standard'),
    headType: pick(gear.headTypeOverride, ['standard', 'draw', 'fade', 'low-spin', 'max-forgiveness'], 'standard'),
    ironCategory: pick(gear.headCategory, ['blade', 'players', 'players-distance', 'game-improvement'], 'players'),
  };
}

export function importedLaunch(payload: EldrickPayload): {
  clubKey: 'driver' | '7iron'; baseline: ClubPreset; params: SwingParams; estimated: string[];
} {
  const clubKey = payload.meta.fittingType === 'iron' ? '7iron' : 'driver';
  const defaults = CLUB_PRESETS[clubKey];
  const sw = payload.swing;
  const estimated: string[] = [];
  const value = (v: number | null | undefined, fallback: number, name: string) => {
    if (v == null) { estimated.push(name); return fallback; }
    return v;
  };
  const clubSpeed = value(sw.clubheadSpeed, defaults.clubSpeed, 'club speed');
  const ballSpeed = value(sw.ballSpeed, clubSpeed * defaults.ballSpeed / defaults.clubSpeed, 'ball speed');
  const launchAngle = value(sw.launchAngle, defaults.launchAngle, 'launch angle');
  const spinRate = value(sw.spinRate, defaults.spinRate, 'spin rate');
  const attackAngle = value(sw.attackAngle, defaults.attackAngle, 'attack angle');
  const clubPath = value(sw.clubPath, 0, 'club path');
  const faceAngle = value(sw.faceAngle, 0, 'face angle');
  const spinAxis = value(unwrapValue(sw.spinAxis), deriveSpinAxisFromImpact(faceAngle, clubPath, launchAngle, attackAngle), 'spin axis');
  if (typeof sw.spinAxis === 'object' && sw.spinAxis?.assumed) estimated.push('spin axis (Core estimate)');
  const launchDirection = value(sw.launchDirection, deriveLaunchDirection(faceAngle, clubPath, launchAngle, attackAngle), 'launch direction');
  const tempo = ['slow', 'medium', 'fast'].includes(payload.swingProfile.tempo ?? '')
    ? payload.swingProfile.tempo as SwingParams['tempo'] : 'medium';
  const transition = ['smooth', 'medium', 'quick'].includes(payload.swingProfile.transition ?? '')
    ? payload.swingProfile.transition as SwingParams['transition'] : 'medium';
  return {
    clubKey, estimated,
    baseline: { name: `ELDRICK ${clubKey}`, clubSpeed, ballSpeed, launchAngle, spinRate, spinAxis, attackAngle, clubPath, faceAngle },
    params: createSwingParams(clubSpeed, ballSpeed, launchAngle, launchDirection, spinRate, spinAxis, attackAngle, clubPath, faceAngle, tempo, transition),
  };
}

/** Same customer delivery for every counterfactual. Do not silently straighten
 * their shot or re-estimate a supplied spin axis when equipment changes. */
export function comparisonDelivery(base: ClubPreset, imported: SwingParams | null) {
  return {
    launchDirection: imported?.launchDirection ?? deriveLaunchDirection(base.faceAngle, base.clubPath, base.launchAngle, base.attackAngle),
    spinAxis: imported?.spinAxis ?? deriveSpinAxisFromImpact(base.faceAngle, base.clubPath, base.launchAngle, base.attackAngle),
    tempo: imported?.tempo ?? 'medium',
    transition: imported?.transition ?? 'medium',
  };
}
